import { type Api } from "api-client";
import { makeAutoObservable } from "mobx";
import { assert, getInitials } from "utils";

import { type SuccessAuthenticate } from "src/types";

export type MaybeAuthAuthenticate = SuccessAuthenticate | null;

export class AuthStore {
  _authenticatedData: MaybeAuthAuthenticate;

  constructor(a: MaybeAuthAuthenticate) {
    this._authenticatedData = a;
    makeAutoObservable(this);
  }

  load(authenticatedData: SuccessAuthenticate) {
    this._authenticatedData = authenticatedData;
  }

  clear() {
    this._authenticatedData = null;
  }

  updateUserName(name: string) {
    if (!this._authenticatedData) {
      return;
    }

    this._authenticatedData = {
      ...this._authenticatedData,
      user: {
        ...this._authenticatedData.user,
        name: name,
      },
    };
  }

  updateUserTimezone(timezone: string) {
    if (!this._authenticatedData) {
      return;
    }

    this._authenticatedData = {
      ...this._authenticatedData,
      user: {
        ...this._authenticatedData.user,
        timezone,
      },
    };
  }

  updateCoachProfileSlug(slug: string) {
    if (!this._authenticatedData?.coach) {
      return;
    }

    this._authenticatedData.coach.profile = { slug };
  }

  updateStripeAccountStatus(
    stripeAccountStatus: Api.Authorize.Output.User["stripeAccountStatus"],
  ) {
    if (!this._authenticatedData) {
      return;
    }

    this._authenticatedData = {
      ...this._authenticatedData,
      user: {
        ...this._authenticatedData.user,
        stripeAccountStatus,
      },
    };
  }

  updateAvatarUrl(avatarUrl: string) {
    if (!this._authenticatedData) {
      return;
    }

    this._authenticatedData = {
      ...this._authenticatedData,
      user: {
        ...this._authenticatedData.user,
        avatarUrl: avatarUrl,
      },
    };
  }

  clearAvatarUrl() {
    if (!this._authenticatedData) {
      return;
    }

    this._authenticatedData = {
      ...this._authenticatedData,
      user: {
        ...this._authenticatedData.user,
        avatarUrl: null,
      },
    };
  }

  get isAuthenticated(): boolean {
    return !!this._authenticatedData;
  }

  get authenticatedData() {
    assert(this._authenticatedData);
    return this._authenticatedData;
  }

  get maybeUser() {
    try {
      return this.user;
    } catch {
      return null;
    }
  }

  get user() {
    assert(this.authenticatedData.user);
    return this.authenticatedData.user;
  }

  /**
   * IANA timezone from Settings.timezone if set, otherwise null. The FE
   * `<TimeProvider>` falls back to the device TZ when this is null.
   */
  get viewerTimezone(): string | null {
    return this.maybeUser?.timezone ?? null;
  }

  get isCoach() {
    try {
      return this.authenticatedData.coach !== null;
    } catch {
      return false;
    }
  }

  get isAthlete() {
    return this.authenticatedData.athlete !== null;
  }

  get coach() {
    assert(this.authenticatedData.coach);
    return this.authenticatedData.coach;
  }

  get coachSafe() {
    try {
      return this.coach;
    } catch {
      return null;
    }
  }

  get athleteSafe() {
    try {
      return this.athlete;
    } catch {
      return null;
    }
  }

  get athlete() {
    assert(this.authenticatedData.athlete);
    return this.authenticatedData.athlete;
  }

  get isAdmin() {
    return this.authenticatedData.user.role === "ADMIN";
  }

  get initials() {
    const name = this.authenticatedData.user.name;
    return getInitials(name);
  }

  get isFreeTier() {
    try {
      return this.user.tier === "FREE";
    } catch {
      return true;
    }
  }

  get hasCoachSubscriptionAccess() {
    try {
      return this.user.tier !== "FREE";
    } catch {
      return false;
    }
  }

  get isOnTrial() {
    try {
      return this.user.tier === "TRIAL";
    } catch {
      return false;
    }
  }

  get hasUsedTrial() {
    try {
      return this.user.trialStartedAt !== null;
    } catch {
      return false;
    }
  }

  get trialEndsAt(): Date | null {
    try {
      const value = this.user.trialEndsAt;
      return value ? new Date(value) : null;
    } catch {
      return null;
    }
  }
}
